import React, { useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import * as mainService from "../../../services/mainService.js";
import store from '../../../redux/store/index.js';


// Styled Components
const DeleteContainer = styled(Box)(({ theme }) => ({
  maxWidth: '600px',
  margin: '0 auto',
  padding: '24px',
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const WarningCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFF3E0',
  border: '1px solid #FFB74D',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
}));

const DangerButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#F44336',
  color: 'white',
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#D32F2F',
  },
  '&:disabled': {
    backgroundColor: '#FFCDD2',
    color: '#FFFFFF',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#FAFAFA',
  },
  '& .MuiInputLabel-root': {
    color: '#666666',
  },
}));

const DeleteAccount = ({ screenWidth }) => {
  const [step, setStep] = useState(1); // 1: password, 2: OTP
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [errors, setErrors] = useState({});

  // Get user data from localStorage or context
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  const validatePassword = () => {
    const newErrors = {};
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOTP = () => {
    const newErrors = {};
    if (!otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(otp.trim())) {
      newErrors.otp = 'OTP must be 6 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePassword()) return;

    setLoading(true);
    try {
      const data = { password };
      const value = { data, token: userData.token };
      
      const res = await mainService.requestDeletePatientApi(value);
      if (res.response_type === "fail") {
        toast.error(res?.response?.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 3000,
          style: { width: "auto", borderRadius: 8 }
        });
      } else {
        setStep(2);
        toast.success("OTP sent to your registered email/phone", {
          position: 'bottom-center',
          closeButton: false,
          autoClose: 3000,
          style: { width: "auto", borderRadius: 8 }
        });
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-center",
        closeButton: false,
        autoClose: 3000,
        style: { width: "auto", borderRadius: 8 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async () => {
    if (!validateOTP()) return;

    setConfirmDialog(true);
  };

  const confirmDelete = async () => {
    const data = { otp };
    let value = { data: data, token: userData.token };
    setLoading(true);
    setConfirmDialog(false);
    console.log("data", data);
    
    const res = await mainService.verifyDeleteAccountApi(value);
    
    if (res.response_type == "fail") {
      setLoading(false);
    } else {
      setLoading(false);
      toast.success(res?.response?.message, {
        position: 'bottom-center',
        closeButton: false,
        autoClose: 1000,
        style: { width: "auto", borderRadius: 8 }
      });
           
      setTimeout(() => {
         store.dispatch({ type: "auth/forceLogoutRequest" });
        // window.location.href = '/';
      }, 3000);
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp('');
    setErrors({});
  };

  return (
    <DeleteContainer>
      {/* Warning Section */}
      <WarningCard>
        <WarningAmberIcon sx={{ color: '#FF9800', fontSize: 28 }} />
        <Box>
          <Typography variant="h6" sx={{ color: '#E65100', fontWeight: 600, mb: 1 }}>
            Warning: This action is highly sensitive!
          </Typography>
          <Typography variant="body2" sx={{ color: '#5D4037', lineHeight: 1.5 }}>
            Deleting your account will permanently erase all of your data, including:
            • Medical records and appointment history
            • Personal information and preferences
            • Emergency contacts
            • Uploaded documents and images
          </Typography>
        </Box>
      </WarningCard>

      {/* Step Indicator */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#2B2B2B', mb: 2 }}>
          Delete Account
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            backgroundColor: step >= 1 ? '#F44336' : '#E0E0E0',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 600
          }}>
            1
          </Box>
          <Box sx={{ 
            flex: 1, 
            height: 2, 
            backgroundColor: step >= 2 ? '#F44336' : '#E0E0E0',
            borderRadius: 1 
          }} />
          <Box sx={{ 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            backgroundColor: step >= 2 ? '#F44336' : '#E0E0E0',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 600
          }}>
            2
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Step 1: Password Verification */}
      {step === 1 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: '#2B2B2B' }}>
            Step 1: Verify Your Password
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666666' }}>
            Please enter your current password to proceed with account deletion.
          </Typography>
          
          <StyledTextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Current Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors({ ...errors, password: '' });
            }}
            error={!!errors.password}
            helperText={errors.password}
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{ flex: 1, borderRadius: '8px', py: 1.5 }}
            >
              Cancel
            </Button>
            <DangerButton
              onClick={handlePasswordSubmit}
              disabled={loading || !password.trim()}
              sx={{ flex: 1, position: 'relative' }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Continue'
              )}
            </DangerButton>
          </Box>
        </Box>
      )}

      {/* Step 2: OTP Verification */}
      {step === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: '#2B2B2B' }}>
            Step 2: Verify OTP
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666666' }}>
            We've sent a 6-digit verification code to your registered email/phone number.
          </Typography>

          <StyledTextField
            fullWidth
            label="Enter 6-digit OTP"
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtp(value);
              if (errors.otp) setErrors({ ...errors, otp: '' });
            }}
            error={!!errors.otp}
            helperText={errors.otp}
            sx={{ mb: 3 }}
            inputProps={{ 
              maxLength: 6,
              style: { textAlign: 'center', fontSize: '18px', letterSpacing: '4px' }
            }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ flex: 1, borderRadius: '8px', py: 1.5 }}
            >
              Back
            </Button>
            <DangerButton
              onClick={handleOTPSubmit}
              disabled={loading || !otp.trim() || otp.length !== 6}
              sx={{ flex: 1 }}
            >
              Delete Account
            </DangerButton>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="text"
              onClick={handlePasswordSubmit}
              sx={{ color: '#1976D2', textDecoration: 'underline' }}
            >
              Didn't receive OTP? Resend
            </Button>
          </Box>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#F44336' }}>
          <DeleteForeverIcon />
          Final Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you absolutely sure you want to delete your account?
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            All your data will be completely <strong>removed</strong> from our servers.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog(false)}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <DangerButton
            onClick={confirmDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteForeverIcon />}
          >
            {loading ? 'Deleting...' : 'Delete Forever'}
          </DangerButton>
        </DialogActions>
      </Dialog>
    </DeleteContainer>
  );
};

export default DeleteAccount;